import React, { useState, useMemo } from 'react';
import { Table, ChevronLeft, ChevronRight, Download, Calendar, DollarSign, TrendingDown, TrendingUp } from 'lucide-react';
import Button from '../../../components/ui/Button';

const AmortizationTable = ({ scheduleData }) => {
  // Always call hooks at the top level - never conditionally
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const paginatedData = useMemo(() => {
    if (!scheduleData || scheduleData.length === 0) {
      return {
        currentItems: [],
        totalPages: 0,
        totalItems: 0
      };
    }

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = scheduleData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(scheduleData.length / itemsPerPage);

    return {
      currentItems,
      totalPages,
      totalItems: scheduleData.length
    };
  }, [scheduleData, currentPage, itemsPerPage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const generatePageNumbers = () => {
    const { totalPages } = paginatedData;
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = Math.max(1, currentPage - halfVisible);
      let end = Math.min(totalPages, start + maxVisiblePages - 1);
      
      if (end - start + 1 < maxVisiblePages) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const handleExportCSV = () => {
    if (!scheduleData || scheduleData.length === 0) return;
    
    const headers = ['Payment #', 'Payment Date', 'Payment Amount', 'Interest', 'Principal', 'Balance'];
    const csvContent = [
      headers.join(','),
      ...scheduleData.map(payment => [
        payment.paymentNumber,
        formatDate(payment.paymentDate),
        payment.paymentAmount.toFixed(2),
        payment.interestPayment.toFixed(2),
        payment.principalPayment.toFixed(2),
        payment.remainingBalance.toFixed(2)
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'amortization_schedule.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Early return after all hooks are called
  if (!scheduleData || scheduleData.length === 0) {
    return (
      <div className="card-professional-elevated">
        <div className="card-professional-header">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Table className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-text-primary">Amortization Schedule</h3>
              <p className="text-sm text-text-secondary">Payment breakdown and schedule</p>
            </div>
          </div>
        </div>
        
        <div className="card-professional-content">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-surface rounded-full flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-text-secondary" />
            </div>
            <h4 className="text-lg font-medium text-text-primary mb-2">No Schedule Generated</h4>
            <p className="text-text-secondary max-w-md">
              Enter loan details and click "Calculate Loan" to generate a detailed amortization schedule.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { currentItems, totalPages, totalItems } = paginatedData;

  // Calculate summary statistics
  const totalPayments = scheduleData.reduce((sum, payment) => sum + payment.paymentAmount, 0);
  const totalInterest = scheduleData.reduce((sum, payment) => sum + payment.interestPayment, 0);
  const totalPrincipal = scheduleData.reduce((sum, payment) => sum + payment.principalPayment, 0);

  return (
    <div className="card-professional-elevated">
      <div className="card-professional-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Table className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-text-primary">Amortization Schedule</h3>
              <p className="text-sm text-text-secondary">
                {totalItems} payments • {formatCurrency(totalPayments)} total
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="btn-professional btn-outline"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-6 py-4 bg-gradient-to-r from-surface/30 to-surface/10 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Total Payments</p>
              <p className="text-lg font-semibold text-text-primary data-display-currency">
                {formatCurrency(totalPayments)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-warning" />
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Total Interest</p>
              <p className="text-lg font-semibold text-warning data-display-currency">
                {formatCurrency(totalInterest)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-4 h-4 text-success" />
            </div>
            <div>
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Total Principal</p>
              <p className="text-lg font-semibold text-success data-display-currency">
                {formatCurrency(totalPrincipal)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="card-professional-content">
        {/* Table Controls */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-text-primary">Show</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="select-professional text-sm py-1 px-2 min-w-0"
            >
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={36}>36</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm text-text-secondary">payments</span>
          </div>
          <div className="text-sm text-text-secondary">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} payments
          </div>
        </div>

        {/* Table */}
        <div className="table-professional-container">
          <table className="table-professional">
            <thead>
              <tr>
                <th className="text-left">#</th>
                <th className="text-left">Payment Date</th>
                <th className="text-right">Payment Amount</th>
                <th className="text-right">Interest</th>
                <th className="text-right">Principal</th>
                <th className="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((payment, index) => (
                <tr key={payment.paymentNumber} className="hover:bg-surface/50 transition-colors">
                  <td className="font-medium text-text-primary">{payment.paymentNumber}</td>
                  <td className="text-text-primary">{formatDate(payment.paymentDate)}</td>
                  <td className="text-right data-display-currency font-medium text-text-primary">
                    {formatCurrency(payment.paymentAmount)}
                  </td>
                  <td className="text-right data-display-currency text-warning">
                    {formatCurrency(payment.interestPayment)}
                  </td>
                  <td className="text-right data-display-currency text-success">
                    {formatCurrency(payment.principalPayment)}
                  </td>
                  <td className="text-right data-display-currency font-semibold text-text-primary">
                    {formatCurrency(payment.remainingBalance)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-professional btn-outline"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {generatePageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm rounded-lg font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-primary text-primary-foreground shadow-soft' 
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-professional btn-outline"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmortizationTable;